import { parseHHMM, type WorkCalendar } from '@hana/work-calendar';
import { presenceBetween } from '../data/logic';
import type { MeeAttendance, MeeRequest } from '../data/types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

/** 2026-10-30 → 30/10 */
export const dm = (day: string): string => `${day.slice(8, 10)}/${day.slice(5, 7)}`;
export const dayNo = (day: string): number => Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10)) / 86_400_000;
export const monthAbbr = (day: string): string => MONTHS[Number(day.slice(5, 7)) - 1]!;
/** Thứ trong tuần của ngày: T2 … CN. */
export const dowOf = (day: string): string => DOW[(((dayNo(day) + 4) % 7) + 7) % 7]!;

/** Số có dấu phẩy thập phân kiểu Việt: 0,5 */
/** "T5 22/Oct/2026" — nhãn ngày đầy đủ như board. */
export const fdw = (day: string): string => `${dowOf(day)} ${day.slice(8, 10)}/${monthAbbr(day)}/${day.slice(0, 4)}`;
export const num = (n: number): string => String(Math.round(n * 100) / 100).replace('.', ',');
export const hm = (min: number): string => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

export function rangeLabel(r: Pick<MeeRequest, 'from' | 'to' | 'half' | 't0' | 't1'>): string {
  if (r.t0 && r.t1) return `${dm(r.from)} · ${r.t0}–${r.t1}`;
  const half = r.half === 'AM' ? ' · sáng' : r.half === 'PM' ? ' · chiều' : '';
  return r.to && r.to !== r.from ? `${dm(r.from)} – ${dm(r.to)}` : `${dm(r.from)}${half}`;
}

export function qtyLabel(r: Pick<MeeRequest, 'type' | 'qty' | 't0' | 't1'>, ot: number): string {
  if (r.type === 'ot') return `${num(ot)} giờ`;
  if (r.type === 'fix') return '—';
  return `${num(r.qty ?? 0)} ngày`;
}

export interface HolidayRow {
  name: string;
  from: string;
  to: string;
  days: number;
  company: boolean;
  /** số ngày dương lịch từ hôm nay tới ngày bắt đầu (0 = đang diễn ra / hôm nay) */
  inDays: number;
}

/** Lễ sắp tới (kể cả đang diễn ra) từ lịch công ty — nguồn duy nhất tblClientInfo. */
export function upcomingHolidays(cal: WorkCalendar, today: string, limit = 3): HolidayRow[] {
  return cal.config.holidays
    .filter((h) => h.to >= today)
    .sort((a, b) => a.from.localeCompare(b.from))
    .slice(0, limit)
    .map((h) => ({
      name: h.name,
      from: h.from,
      to: h.to,
      days: dayNo(h.to) - dayNo(h.from) + 1,
      company: h.company,
      inDays: Math.max(0, dayNo(h.from) - dayNo(today)),
    }));
}

export interface TodayCard {
  /** các đoạn thanh thời gian: làm / nghỉ trưa, % trên cả ngày */
  segs: { kind: 'work' | 'break'; pct: number }[];
  /** % thanh đã trôi qua tới giờ hiện tại */
  nowPct: number;
  ticks: string[];
  /** mốc giờ trên thanh, vị trí % trên cả ngày */
  marks: { t: string; pct: number }[];
  in: string;
  out: string;
  presence: number;
  shift: string;
  /** hôm nay không phải ngày làm việc */
  off: boolean;
}

/** Thẻ "Chấm công hôm nay": ca theo lịch công ty; ngày làm việc không có dòng ghi riêng = đã vào đúng giờ, chưa ra. */
export function todayCard(cal: WorkCalendar, today: string, now: string, rec?: MeeAttendance): TodayCard {
  const shifts = cal.shiftsOf(today);
  if (!shifts.length) return { segs: [], nowPct: 0, ticks: [], marks: [], in: '', out: '', presence: 0, shift: '', off: true };
  const start = shifts[0]![0];
  const end = shifts[shifts.length - 1]![1];
  const span = end - start;
  const segs: TodayCard['segs'] = [];
  shifts.forEach((sh, i) => {
    if (i > 0) segs.push({ kind: 'break', pct: ((sh[0] - shifts[i - 1]![1]) / span) * 100 });
    segs.push({ kind: 'work', pct: ((sh[1] - sh[0]) / span) * 100 });
  });
  const inT = rec ? rec.in : hm(start - 5);
  const out = rec ? rec.out : '';
  const nowMin = parseHHMM(now);
  const nowPct = Math.min(100, Math.max(0, ((nowMin - start) / span) * 100));
  const upTo = out || (nowMin >= end ? hm(end) : now);
  return {
    segs,
    nowPct,
    ticks: [hm(start), ...shifts.slice(1).map((s) => hm(s[0])), hm(end)],
    marks: [start, ...shifts.slice(1).flatMap((s, i) => [shifts[i]![1], s[0]]), end].map((m) => ({ t: hm(m), pct: ((m - start) / span) * 100 })),
    in: inT,
    out,
    presence: presenceBetween(cal, today, inT, upTo),
    shift: `${hm(start)}–${hm(end)}`,
    off: false,
  };
}
